"use client"
import { cn } from "@/utils/helpers"
import { forwardRef, useLayoutEffect, useState, useMemo } from "react"
import { TIconName } from "./IconUtil"

export const ICON_FONT = '300 24px "Material Symbols Outlined"'
export type TIcon = {
  icon: TIconName
  fill?: boolean
  weight?: number
  gradient?: number
  opticalSize?: number
  className?: string
  size?: "xs" | "sm" | "md" | "lg" | "xl" | `${number}${string}`
  maxSize?: `${number}${string}`
}

/**
 * Icon component to render Material Symbols icons with customizable styles.
 *
 * @param {TIconProps} props - The props for the Icon component.
 * @param {string} props.icon - The name of the icon to render.
 * @param {boolean} [props.fill=false] - Whether the icon should be filled. Defaults to false.
 * @param {number} [props.weight=400] - The weight of the icon. Defaults to 400.
 * @param {number} [props.gradient=0] - The gradient of the icon. Defaults to 0.
 * @param {number} [props.opticalSize=24] - The optical size of the icon. Defaults to 24.
 * @param {string} [props.className] - Additional CSS classes to apply to the icon.
 * @param {"sm" | "md" | "lg"} [props.size="md"] - The size of the icon. Defaults to "md".
 * @returns {JSX.Element} The rendered icon.
 */
const Icon = forwardRef<HTMLSpanElement, TIcon>(
  (
    {
      icon,
      fill,
      gradient,
      opticalSize,
      weight,
      size = "md",
      maxSize,
      className,
      ...props
    },
    ref,
  ) => {
    const [ready, setReady] = useState(() =>
      typeof document !== "undefined" ? document.fonts.check(ICON_FONT) : false,
    )
    useLayoutEffect(() => {
      if (ready) return
      const syncReady = () => {
        setReady(document.fonts.check(ICON_FONT))
      }
      if (document.fonts.check(ICON_FONT)) {
        setReady(true)
      }
      void document.fonts.load(ICON_FONT).finally(() => {
        syncReady()
      })
    }, [])

    const sizeValue: string = useMemo(() => {
      let res: string
      switch (size) {
        case "xs":
          res = "1.20rem"
          break
        case "sm":
          res = "1.5rem"
          break
        case "md":
          res = "1.5rem"
          break
        case "lg":
          res = "1.5rem"
          break
        case "xl":
          res = "2.5rem"
          break
        default:
          res = size
          break
      }
      if (maxSize) {
        res = `min(${maxSize},${res})`
      }
      return res
    }, [size, maxSize])
    if (!ready)
      return (
        <span
          ref={ref}
          style={{ width: sizeValue, height: sizeValue }}
          className={cn("inline-block select-none p-0.5", className)}
          {...props}
        >
          <span className="bg-outline-variant inline-block h-full w-full animate-pulse select-none rounded-sm" />
        </span>
      )
    return (
      <span
        ref={ref}
        style={{
          fontVariationSettings: `'FILL' ${fill ? 1 : 0}, 'wght' ${weight ?? 300}, 'GRAD' ${gradient ?? 0}, 'opsz' ${opticalSize ?? 24}`,
          fontSize: sizeValue,
          lineHeight: sizeValue,
        }}
        className={cn(
          "material-symbols-outlined-custom select-none",
          className,
        )}
        {...props}
      >
        {icon}
      </span>
    )
  },
)

Icon.displayName = "Icon"

export { Icon }
